import React from 'react';
import {lazy} from 'react';
import {Outlet, Navigate} from 'react-router-dom';
// auth
import {AuthGuard} from 'packages/common/guards';
import {RoleBasedGuard, AdminSectionGuard} from 'packages/common/guards';
// layouts
import DashboardLayout from 'packages/common/layouts/admin/layout';
// components
import {SplashScreen, BootSuspense} from 'packages/common/components/loading-screen';
// roles
import {paths} from 'packages/common/routes/paths';
import {routeRoles} from 'packages/common/utils/rolesConfig';
// context
import {AuthProvider} from 'packages/common/contexts/auth-context';
import {AdminAccessProvider} from 'packages/common/contexts/admin-access-context';
import SessionProvider from 'packages/common/providers/SessionProvider';

// ----------------------------------------------------------------------

const AdminPageDashboard = lazy(() => import('apps/dashboard/pages/admin/dashboard/view/DashboardPage'));
const AdminPageAnalytics = lazy(() => import('apps/dashboard/pages/admin/analytics/view/AnalyticsPage'));
const AdminPageAccounts = lazy(() => import('apps/dashboard/pages/admin/accounts/view/AccountsPage'));
const AdminPageAccountDetails = lazy(() => import('apps/dashboard/pages/admin/accounts/details/AccountDetailsView'));
const AdminPageCompanies = lazy(() => import('apps/dashboard/pages/admin/companies/view/CompaniesPage'));
const AdminPageCreateCompany = lazy(() => import('apps/dashboard/pages/admin/companies/view/CreateCompanyPage'));
const AdminPageCRM = lazy(() => import('apps/dashboard/pages/admin/crm/view/CRMPage'));
const AdminPageInvoices = lazy(() => import('apps/dashboard/pages/admin/invoices/view/InvoicesPage'));
const AdminPageSubscriptions = lazy(() => import('apps/dashboard/pages/admin/subscriptions/view/SubscriptionsPage'));
const AdminPageEmailLogs = lazy(() => import('apps/dashboard/pages/admin/emails/view/EmailLogsPage'));
const AdminPageNewsletterQuotas = lazy(() => import('apps/dashboard/pages/admin/newsletter-quotas/view/NewsletterQuotasPage'));
const AdminPageTeam = lazy(() => import('apps/dashboard/pages/admin/team/view/TeamPage'));
const AdminPageCommissions = lazy(() => import('apps/dashboard/pages/admin/commissions/view/CommissionsPage'));
const AdminPageSms = lazy(() => import('apps/dashboard/pages/admin/sms/view/SmsPage'));
const AdminPageSettings = lazy(() => import('apps/dashboard/pages/admin/settings/view/SettingsPage'));

/**
 * Enveloppe une page admin dans le guard de section correspondant.
 *
 * Le gating par section est piloté par le registre config/adminSections —
 * même clé que le requireAdminSection du router backend.
 *
 * @param {string} section - Clé de section du registre
 * @param {JSX.Element} element - Page à protéger
 * @return {JSX.Element} Page gardée
 */
const guarded = (section, element) => <AdminSectionGuard section={section}>{element}</AdminSectionGuard>;

// ----------------------------------------------------------------------

export const adminRoutes = [
  {
    path: '/admin',
    element: (
      <AuthProvider>
        <AdminAccessProvider>
          <AuthGuard fallback={<SplashScreen />}>
            <SessionProvider>
              <RoleBasedGuard roles={routeRoles[paths.admin.root]}>
                <DashboardLayout>
                  <BootSuspense>
                    <Outlet />
                  </BootSuspense>
                </DashboardLayout>
              </RoleBasedGuard>
            </SessionProvider>
          </AuthGuard>
        </AdminAccessProvider>
      </AuthProvider>
    ),
    children: [
      {element: <AdminPageDashboard />, index: true},
      {path: 'dashboard', element: <AdminPageDashboard />},

      // === ÉTABLISSEMENTS ===
      {
        path: 'merchants',
        children: [
          // Redirection vers accounts par défaut
          {index: true, element: <Navigate to="accounts" replace />},
          // Accounts
          {
            path: 'accounts',
            children: [
              {index: true, element: guarded('accounts', <AdminPageAccounts />)},
              {path: ':accountId', element: guarded('accounts', <AdminPageAccountDetails />)},
            ],
          },
          // Companies
          {
            path: 'companies',
            children: [
              {index: true, element: guarded('companies', <AdminPageCompanies />)},
              {path: 'create', element: guarded('companies', <AdminPageCreateCompany />)},
              {path: ':id', element: guarded('companies', <AdminPageCompanies />)},
            ],
          },
          // Subscriptions
          {
            path: 'subscriptions',
            element: guarded('subscriptions', <AdminPageSubscriptions />),
          },
        ],
      },

      // === ANALYTICS ===
      {
        path: 'analytics',
        element: guarded('analytics', <AdminPageAnalytics />),
      },

      // === FINANCES ===
      {
        path: 'finances',
        children: [
          {index: true, element: <Navigate to="invoices" replace />},
          {path: 'invoices', element: guarded('invoices', <AdminPageInvoices />)},
          {path: 'commissions', element: guarded('commissions', <AdminPageCommissions />)},
        ],
      },

      // === CRM ===
      {
        path: 'crm',
        element: guarded('crm', <AdminPageCRM />),
      },

      // === COMMUNICATIONS ===
      {
        path: 'communications',
        children: [
          {index: true, element: <Navigate to="emails" replace />},
          {path: 'emails', element: guarded('emails', <AdminPageEmailLogs />)},
          {
            // Agrège le compte Amazon et TOUS les établissements : réservée au
            // super admin, comme la page SMS, et non rattachée à une section
            // attribuable qui ne saurait pas la restreindre à un portefeuille.
            path: 'newsletter-quotas',
            element: (
              <AdminSectionGuard superAdminOnly>
                <AdminPageNewsletterQuotas />
              </AdminSectionGuard>
            ),
          },
          {
            path: 'sms',
            element: (
              <AdminSectionGuard superAdminOnly>
                <AdminPageSms />
              </AdminSectionGuard>
            ),
          },
        ],
      },

      // === ÉQUIPE (super admin uniquement) ===
      {
        path: 'team',
        element: (
          <AdminSectionGuard superAdminOnly>
            <AdminPageTeam />
          </AdminSectionGuard>
        ),
      },

      // === RÉGLAGES PLATEFORME (super admin uniquement) ===
      {
        path: 'settings',
        element: (
          <AdminSectionGuard superAdminOnly>
            <AdminPageSettings />
          </AdminSectionGuard>
        ),
      },
    ],
  },
];
